import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationIcon } from '@heroicons/react/outline'
import TitleDivider from 'app/components/TitleDivider'
import { getStakedValueFromTokens } from 'app/features/earn/earn.module'
import {
  Status,
  unStakeTokens,
  userCanGetReward,
} from 'app/features/earn/earnSlice'
import { useAppDispatch, useAppSelector } from 'app/hooks/hooks'
import { secondsToDhms } from 'app/utils/web3'

type UnStakeHelper = {
  text: JSX.Element
}

const unStakeHelperText: UnStakeHelper[] = [
  { text: <>Connect your wallet holding XODEX to enable un-staking.</> },
  {
    text: (
      <>
        Enter the amount of XODEX you wish to un-stake, or press the{' '}
        <span className="text-cerulean-600 text-bold">MAX</span> button to
        un-stake all of your XODEX.
      </>
    ),
  },
  {
    text: (
      <>
        Press the Unstake button. First approve the dApp to access your XODEX.
        Then approve the staking transaction.
      </>
    ),
  },
  {
    text: (
      <>
        Once transaction is approved. Your XODEX will be transfered back to your
        wallet.
      </>
    ),
  },
]

const Staked = () => {
  const {
    stakedTokens,
    stakedValue,
    unStakingStatus,
    tokenPrice,
    timeLeft = 0,
    earned,
  } = useAppSelector((state) => state.earn)

  const { web3 } = useAppSelector((state) => state.web3)

  const dispatch = useAppDispatch()

  const [unStakeInput, setUnstakeInput] = useState<number>()
  const [unStakeInputValue, setUnStakeInputValue] = useState<number>(0)

  const [modalOpen, setModalOpen] = useState(false)

  const cancelButtonRef = useRef(null)

  useEffect(() => {
    const getAsValue = async () => {
      if (web3 && tokenPrice && unStakeInput) {
        const value = await getStakedValueFromTokens(
          web3,
          unStakeInput,
          tokenPrice
        )

        setUnStakeInputValue(value)
      }
    }
    getAsValue().catch((error) => {
      console.error(error)
      // TODO handle error
    })
  }, [unStakeInput])

  if (!stakedTokens || !stakedValue) {
    return null
  }

  const getMaxStakedTokens = () => {
    setUnstakeInput(Math.trunc(Number(stakedTokens)))
  }

  const handleUnstakeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Math.trunc(Number(e.target.value))

    if (val > 0) {
      if (stakedTokens && val >= stakedTokens) {
        setUnstakeInput(stakedTokens)
        return
      }
      setUnstakeInput(val)
      return
    }
    if (val < 0) {
      setUnstakeInput(0)
      return
    }
    setUnstakeInput(undefined)
  }

  const unStake = () => {
    if (unStakeInput) {
      dispatch(unStakeTokens(unStakeInput))
        .unwrap()
        .then(() => {
          setUnstakeInput(0)
          setUnStakeInputValue(0)
        })
        .catch((error) => {
          console.log(error)
          // TODO handle error
        })
    }
  }

  const handleUnStake = async () => {
    try {
      const { canGetReward } = await dispatch(userCanGetReward()).unwrap()
      if (!canGetReward) {
        setModalOpen(true)
        return
      }
      unStake()
    } catch (error) {
      // TODO handle error
    }
  }

  const UnStakeModal = () => {
    return (
      <Transition.Root show={modalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={setModalOpen}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-ebony-clay-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative inline-block align-bottom bg-ebony-clay-600 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg leading-6 font-medium text-white"
                    >
                      Unstaking early
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-400">
                        Are you sure you want to unstake your tokens early?
                        <br />
                        You will lose any rewards for this reward period. <br />
                        This cannot be undone
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      unStake()
                      setModalOpen(false)
                    }}
                  >
                    Unstake
                  </button>
                  <button
                    type="button"
                    className="w-full mt-3 inline-flex items-center justify-center px-4 py-2 shadow-sm  rounded-md    focus:ring-offset-2  border focus:outline-none  focus:ring-4  font-medium  text-sm  bg-ebony-clay-600 text-white border-gray-600 hover:bg-ebony-clay-700 hover:border-gray-600 focus:ring-gray-700
                    sm:mt-0 sm:w-auto sm:text-sm
                    "
                    onClick={() => setModalOpen(false)}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    )
  }

  return (
    <div className="mt-20">
      <div className="grid grid-cols-1 gap-4 items-start lg:grid-cols-3 lg:gap-8">
        {/* Left column */}
        <div className="grid grid-cols-1 gap-4 lg:col-span-2">
          <section aria-labelledby="section-unstaking">
            <div className="overflow-hidden ">
              <div className="">
                <div>
                  {/* <TitleDivider title="Unstake" /> */}
                  <div className="bg-ebony-clay-600 p-px overflow-hidden shadow-md rounded-lg bg-gradient-to-br from-cerulean-300 to-cerulean-600">
                    <div className="relative transition-all ease-in duration-75 bg-ebony-clay-600 rounded-lg p-5">
                      <TitleDivider title="Unstake" color="cerulean-500" />

                      <div className="px-4 py-5 sm:px-6 text-white text-center">
                        <div className="text-center">
                          <div>
                            <dl className="grid gap-5 grid-cols-2">
                              <div className="px-1 py-1  rounded-lg overflow-hidden sm:p-2">
                                <dt className="text-sm font-medium text-gray-400 truncate">
                                  Staked value
                                </dt>
                                <dd className="mt-1 text-xl sm:text-3xl font-semibold text-white">
                                  ${stakedValue.toFixed(2)}
                                </dd>
                              </div>

                              <div className="px-1 py-1  rounded-lg overflow-hidden sm:p-2">
                                <dt className="text-sm font-medium text-gray-400 truncate">
                                  Staked tokens
                                </dt>
                                <dd className="mt-1 text-xl sm:text-3xl font-semibold text-white">
                                  {stakedTokens.toFixed(0)}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      </div>

                      {timeLeft === 0 && (
                        <>
                          <div className="px-4 py-5 sm:p-6">
                            <div>
                              <label
                                htmlFor="unStakeAmount"
                                className="block mb-2 text-sm font-medium text-gray-300"
                              >
                                Amount to unstake
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  id="unStakeAmount"
                                  className="block w-full p-4 pl-10 text-sm text-white bg-gray-800 border border-gray-600 rounded-lg focus:ring-cerulean-500 focus:border-cerulean-500 "
                                  placeholder="0"
                                  required
                                  onChange={handleUnstakeInputChange}
                                  value={unStakeInput}
                                />
                                <button
                                  type="submit"
                                  className="text-white absolute right-2.5 bottom-2.5 bg-cerulean-600 hover:bg-cerulean-700  focus:outline-none focus:ring-cerulean-300 focus:ring-0 font-medium rounded-lg text-sm px-4 py-2 "
                                  onClick={getMaxStakedTokens}
                                >
                                  Max
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="text-center">
                            <dt className="text-sm font-medium text-gray-400 truncate">
                              Unstake amount
                            </dt>
                            <dd className="mt-1 text-3xl font-semibold text-white">
                              ${unStakeInputValue.toFixed(2)}
                            </dd>
                          </div>

                          <div className="px-4 py-4 sm:px-6 text-center">
                            <button
                              type="button"
                              className="mt-5 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-cerulean-500 hover:bg-cerulean-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cerulean-400"
                              onClick={() => {
                                handleUnStake()
                              }}
                              disabled={unStakingStatus === Status.loading}
                            >
                              {unStakingStatus === Status.loading && (
                                <svg
                                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                              )}
                              Claim rewards & Unstake
                            </button>
                          </div>
                        </>
                      )}

                      {timeLeft > 0 && (
                        <div>
                          <div className="px-4 py-4 flex items-center text-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-10 w-10 text-red-500 mr-2 flex-shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <p
                              id="helper-text-explanation"
                              className="text-sm text-white"
                            >
                              {/* Unstaking tokens early will forfeit current
                              rewards and be used to burn XODEX tokens. */}
                              You must wait until your staking period ends
                              before you can unstake your tokens.
                            </p>
                          </div>
                          <div className="text-center">
                            <div>
                              <dl className="mt-5 grid gap-5 grid-cols-2">
                                <div className="px-1 py-1 rounded-lg overflow-hidden sm:p-2">
                                  <dt className="text-sm font-medium text-gray-400 truncate">
                                    Current rewards
                                  </dt>
                                  <dd className="mt-1 text-3xl font-semibold text-white">
                                    ${earned?.toFixed(2)}
                                  </dd>
                                </div>

                                <div className="px-1 py-1  rounded-lg overflow-hidden sm:p-2">
                                  <dt className="text-sm font-medium text-gray-400 truncate">
                                    Time left
                                  </dt>
                                  <dd className="mt-1 text-md sm:text-lg font-semibold text-white">
                                    {timeLeft > 0
                                      ? secondsToDhms(timeLeft)
                                      : '-'}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Right column */}
        <div className="grid grid-cols-1 gap-4">
          <section aria-labelledby="section-stake-help">
            <div className="overflow-hidden">
              <div className="">
                <div className="">
                  <h2 className="text-cerulean-600 text-lg font-medium mb-3">
                    How to un-stake
                  </h2>
                  {unStakeHelperText.map((item, index) => (
                    <div>
                      <div className="flex relative pb-5">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full inline-flex justify-center text-white relative z-10">
                          {index + 1}
                        </div>
                        <div className="flex-grow ml-3">
                          <p className="leading text-gray-400">{item.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <p className="text-white">
                    Un-staking will automatically claim any current rewards
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <UnStakeModal />
    </div>
  )
}

export default Staked
